import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Plus, Calendar, Euro, Clock, User, CheckCircle, XCircle,
  AlertTriangle, Edit, Send, Loader2, RefreshCw, History, FileSignature,
  Download, Eye, Printer, Building2, Mic, Square, Play, Trash2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Using api from lib/axios

const MemberContractsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [contracts, setContracts] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  
  // Create contract modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [contractType, setContractType] = useState('monthly');
  const [selectedMember, setSelectedMember] = useState('');
  const [pricingType, setPricingType] = useState('hourly'); // 'hourly' or 'fixed'
  const [hourlyRate, setHourlyRate] = useState('20');
  const [fixedAmount, setFixedAmount] = useState('');
  const [conditions, setConditions] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [brief, setBrief] = useState('');
  const [briefAudioUrl, setBriefAudioUrl] = useState('');
  const [briefAudioTranscript, setBriefAudioTranscript] = useState('');
  const [deadline, setDeadline] = useState('');
  const [budgetHours, setBudgetHours] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [creating, setCreating] = useState(false);
  // Audio recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  // Prestataire info
  const [prestataireStatut, setPrestataireStatut] = useState('Micro-entrepreneur');
  const [prestataireSiret, setPrestataireSiret] = useState('');
  const [prestataireAddress, setPrestataireAddress] = useState('');
  
  // Contract document modal
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentHtml, setDocumentHtml] = useState('');
  const [loadingDocument, setLoadingDocument] = useState(false);
  
  // View/Negotiate contract modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [negotiateAction, setNegotiateAction] = useState('');
  const [negotiateReason, setNegotiateReason] = useState('');
  const [proposedRate, setProposedRate] = useState('');
  const [proposedBudget, setProposedBudget] = useState('');
  const [proposedDeadline, setProposedDeadline] = useState('');
  const [negotiating, setNegotiating] = useState(false);

  // Edit contract modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Duplicate contract modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatingContract, setDuplicatingContract] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({});
  const [duplicating, setDuplicating] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingContract, setDeletingContract] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Terminate confirmation
  const [showTerminateConfirm, setShowTerminateConfirm] = useState(false);
  const [terminatingContract, setTerminatingContract] = useState(null);
  const [terminateReason, setTerminateReason] = useState('');
  const [terminating, setTerminating] = useState(false);

  const isAgency = user?.role === 'agency' || user?.role === 'project_manager';

  useEffect(() => {
    fetchContracts();
    if (isAgency) {
      fetchMembers();
    }
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await api.get('/member-contracts/');
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Erreur lors du chargement des contrats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      // Fetch both team members and affiliates for contract creation
      const [membersRes, affiliatesRes] = await Promise.all([
        api.get('/users/?role=team_member'),
        api.get('/users/?role=affiliate')
      ]);
      const allMembers = [
        ...(membersRes.data || []).map(m => ({ ...m, member_type: 'team_member' })),
        ...(affiliatesRes.data || []).map(m => ({ ...m, member_type: 'affiliate' }))
      ];
      setMembers(allMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-transcribe after recording stops
        toast.info('Transcription automatique en cours...');
        await autoTranscribeAudio(blob);
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      toast.info('Enregistrement en cours...');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  // Auto-transcribe with blob parameter (for immediate use after recording)
  const autoTranscribeAudio = async (blob) => {
    if (!blob) return;
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'brief_audio.webm');
      
      const response = await axios.post(`${API}/briefs/transcribe-audio`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.transcript) {
        setBriefAudioTranscript(response.data.transcript);
        // Automatically fill the text brief with transcription
        setBrief(prev => {
          if (!prev || prev.trim() === '') {
            return response.data.transcript;
          }
          // Append to existing brief
          return prev + '\n\n' + response.data.transcript;
        });
        toast.success('Audio transcrit et ajouté au brief !');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Erreur lors de la transcription automatique');
    } finally {
      setTranscribing(false);
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'brief_audio.webm');
      
      const response = await axios.post(`${API}/briefs/transcribe-audio`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.transcript) {
        setBriefAudioTranscript(response.data.transcript);
        // Also set the text brief if empty
        if (!brief) {
          setBrief(response.data.transcript);
        }
        toast.success('Audio transcrit avec succès');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Erreur lors de la transcription');
    } finally {
      setTranscribing(false);
    }
  };

  const deleteAudioRecording = () => {
    setAudioBlob(null);
    setBriefAudioUrl('');
    setBriefAudioTranscript('');
  };

  const handleCreateContract = async () => {
    if (!selectedMember) {
      toast.error('Veuillez sélectionner un membre');
      return;
    }
    
    if (pricingType === 'hourly' && !hourlyRate) {
      toast.error('Veuillez renseigner le taux horaire');
      return;
    }
    
    if (pricingType === 'fixed' && !fixedAmount) {
      toast.error('Veuillez renseigner le montant forfaitaire');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        member_id: selectedMember,
        contract_type: contractType,
        pricing_type: pricingType,
        hourly_rate: pricingType === 'hourly' ? parseFloat(hourlyRate) : null,
        fixed_amount: pricingType === 'fixed' ? parseFloat(fixedAmount) : null,
        conditions: conditions || null,
        start_date: startDate ? new Date(startDate).toISOString() : null,
        end_date: endDate ? new Date(endDate).toISOString() : null,
        brief: brief || null,
        brief_audio_url: briefAudioUrl || null,
        brief_audio_transcript: briefAudioTranscript || null,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        budget_hours: budgetHours ? parseFloat(budgetHours) : null,
        budget_amount: pricingType === 'hourly' && budgetHours && hourlyRate 
          ? parseFloat(budgetHours) * parseFloat(hourlyRate) 
          : (pricingType === 'fixed' ? parseFloat(fixedAmount) : null),
        prestataire_statut: prestataireStatut || null,
        prestataire_siret: prestataireSiret || null,
        prestataire_address: prestataireAddress || null
      };
      
      await axios.post(`${API}/member-contracts/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contrat créé et envoyé au membre');
      setShowCreateModal(false);
      resetCreateForm();
      fetchContracts();
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du contrat');
    } finally {
      setCreating(false);
    }
  };

  const viewContractDocument = async (contractId) => {
    setLoadingDocument(true);
    setShowDocumentModal(true);
    try {
      const response = await api.get('/member-contracts/${contractId}/document');
      setDocumentHtml(response.data.html);
    } catch (error) {
      console.error('Error fetching document:', error);
      toast.error('Erreur lors du chargement du document');
      setShowDocumentModal(false);
    } finally {
      setLoadingDocument(false);
    }
  };

  const printContract = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(documentHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const downloadContractPdf = async () => {
    // Open in new window and trigger print (as PDF)
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Contrat de Sous-Traitance</title>
          <style>
            @media print {
              body { margin: 0; padding: 20px; }
            }
          </style>
        </head>
        <body>
          ${documentHtml}
          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleNegotiate = async () => {
    if (!selectedContract || !negotiateAction) return;
    
    setNegotiating(true);
    try {
      const payload = {
        action: negotiateAction,
        reason: negotiateReason || null,
        proposed_hourly_rate: proposedRate ? parseFloat(proposedRate) : null,
        proposed_budget_hours: proposedBudget ? parseFloat(proposedBudget) : null,
        proposed_deadline: proposedDeadline ? new Date(proposedDeadline).toISOString() : null
      };
      
      await axios.post(`${API}/member-contracts/${selectedContract.id}/negotiate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const messages = {
        accept: 'Contrat accepté avec succès',
        reject: 'Contrat refusé',
        modify: 'Demande de modification envoyée'
      };
      toast.success(messages[negotiateAction]);
      setShowDetailModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error negotiating contract:', error);
      toast.error('Erreur lors de la négociation');
    } finally {
      setNegotiating(false);
    }
  };

  const resetCreateForm = () => {
    setContractType('monthly');
    setSelectedMember('');
    setPricingType('hourly');
    setHourlyRate('20');
    setFixedAmount('');
    setConditions('');
    setStartDate('');
    setEndDate('');
    setBrief('');
    setBriefAudioUrl('');
    setBriefAudioTranscript('');
    setAudioBlob(null);
    setDeadline('');
    setBudgetHours('');
    setBudgetAmount('');
    setPrestataireStatut('Micro-entrepreneur');
    setPrestataireSiret('');
    setPrestataireAddress('');
  };

  // Open edit modal
  const openEditModal = (contract, e) => {
    e?.stopPropagation();
    setEditingContract(contract);
    setEditForm({
      contract_type: contract.contract_type,
      pricing_type: contract.pricing_type,
      hourly_rate: contract.hourly_rate || '',
      fixed_amount: contract.fixed_amount || '',
      conditions: contract.conditions || '',
      start_date: contract.start_date?.split('T')[0] || '',
      end_date: contract.end_date?.split('T')[0] || '',
      brief: contract.brief || '',
      deadline: contract.deadline?.split('T')[0] || '',
      budget_hours: contract.budget_hours || '',
      budget_amount: contract.budget_amount || '',
      prestataire_statut: contract.prestataire_statut || 'Micro-entrepreneur',
      prestataire_siret: contract.prestataire_siret || '',
      prestataire_address: contract.prestataire_address || ''
    });
    setShowEditModal(true);
  };

  // Save edited contract
  const handleSaveEdit = async () => {
    if (!editingContract) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/member-contracts/${editingContract.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contrat mis à jour');
      setShowEditModal(false);
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Error updating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Open duplicate modal
  const openDuplicateModal = (contract, e) => {
    e?.stopPropagation();
    setDuplicatingContract(contract);
    setDuplicateForm({
      member_id: contract.member_id,
      contract_type: contract.contract_type,
      pricing_type: contract.pricing_type,
      hourly_rate: contract.hourly_rate || '',
      fixed_amount: contract.fixed_amount || '',
      start_date: '',
      end_date: '',
      deadline: ''
    });
    setShowDuplicateModal(true);
  };

  // Duplicate contract
  const handleDuplicate = async () => {
    if (!duplicatingContract) return;
    
    setDuplicating(true);
    try {
      await axios.post(`${API}/member-contracts/${duplicatingContract.id}/duplicate`, duplicateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contrat dupliqué');
      setShowDuplicateModal(false);
      setDuplicatingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Error duplicating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la duplication');
    } finally {
      setDuplicating(false);
    }
  };

  // Open delete confirmation
  const openDeleteConfirm = (contract, e) => {
    e?.stopPropagation();
    setDeletingContract(contract);
    setShowDeleteConfirm(true);
  };

  // Delete contract
  const handleDelete = async () => {
    if (!deletingContract) return;
    
    setDeleting(true);
    try {
      await api.delete('/member-contracts/${deletingContract.id}');
      toast.success('Contrat supprimé');
      setShowDeleteConfirm(false);
      setDeletingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Open terminate confirmation
  const openTerminateConfirm = (contract, e) => {
    e?.stopPropagation();
    setTerminatingContract(contract);
    setTerminateReason('');
    setShowTerminateConfirm(true);
  };

  // Terminate contract
  const handleTerminate = async () => {
    if (!terminatingContract) return;
    
    setTerminating(true);
    try {
      await axios.post(`${API}/member-contracts/${terminatingContract.id}/terminate`, 
        { reason: terminateReason || 'Résilié par l\'agence' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contrat résilié');
      setShowTerminateConfirm(false);
      setTerminatingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Error terminating contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la résiliation');
    } finally {
      setTerminating(false);
    }
  };

  // Check if contract can be edited
  const canEdit = (contract) => {
    const editableStatuses = ['draft', 'pending_agency', 'pending_member', 'negotiation'];
    return isAgency && editableStatuses.includes(contract.status);
  };

  // Check if contract can be deleted
  const canDelete = (contract) => {
    const deletableStatuses = ['draft', 'rejected', 'terminated', 'expired'];
    return isAgency && deletableStatuses.includes(contract.status);
  };

  // Check if contract can be terminated
  const canTerminate = (contract) => {
    return isAgency && contract.status === 'active';
  };

  const openDetailModal = (contract) => {
    setSelectedContract(contract);
    setNegotiateAction('');
    setNegotiateReason('');
    setProposedRate('');
    setProposedBudget('');
    setProposedDeadline('');
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
      pending_member: { label: 'En attente membre', color: 'bg-amber-100 text-amber-700' },
      pending_member_sign: { label: 'À signer par membre', color: 'bg-orange-100 text-orange-700' },
      pending_agency: { label: 'En attente agence', color: 'bg-blue-100 text-blue-700' },
      negotiation: { label: 'En négociation', color: 'bg-purple-100 text-purple-700' },
      active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700' },
      expired: { label: 'Expiré', color: 'bg-slate-100 text-slate-700' },
      terminated: { label: 'Résilié', color: 'bg-red-100 text-red-700' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getTypeBadge = (type) => {
    return type === 'monthly' 
      ? <Badge variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" />Créatif</Badge>
      : <Badge variant="outline" className="gap-1"><FileText className="w-3 h-3" />Sous-traitant</Badge>;
  };

  const filteredContracts = contracts.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ['pending_member', 'pending_member_sign', 'pending_agency', 'negotiation'].includes(c.status);
    if (activeTab === 'active') return c.status === 'active';
    if (activeTab === 'ended') return ['expired', 'terminated', 'completed'].includes(c.status);
    if (activeTab === 'monthly') return c.contract_type === 'monthly';
    if (activeTab === 'mission') return c.contract_type === 'mission';
    return true;
  });

  const needsAction = (contract) => {
    if (!contract) return false;
    if (isAgency && contract.status === 'pending_agency') return true;
    if (!isAgency && ['pending_member', 'pending_member_sign'].includes(contract.status)) return true;
    return false;
  };
  
  // Determine if member should sign or propose modifications
  const memberShouldSign = (contract) => {
    if (!contract || isAgency) return false;
    // Member should sign if:
    // 1. Initial contract waiting for member signature (pending_member)
    // 2. Agency accepted counter-proposal, now member must sign (pending_member_sign)
    return ['pending_member', 'pending_member_sign'].includes(contract.status);
  };
  
  // Get the appropriate button label for the member
  const getActionButtonLabel = (contract, action) => {
    if (!contract) return 'Confirmer';
    
    // For agency, always show standard labels
    if (isAgency) {
      if (action === 'accept') return 'Accepter la proposition';
      if (action === 'modify') return 'Contre-proposer';
      if (action === 'reject') return 'Refuser';
      return 'Confirmer';
    }
    
    // For member
    if (action === 'accept') {
      // If member is signing (not modifying)
      return 'Signer le contrat';
    }
    if (action === 'modify') {
      return 'Envoyer la proposition';
    }
    if (action === 'reject') {
      return 'Refuser le contrat';
    }
    return 'Confirmer';
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8" style={{ paddingTop: '140px' }}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                {isAgency ? 'Créatifs & Sous-traitants' : 'Mes Contrats'}
              </h1>
              <p className="text-slate-500 mt-1">
                {isAgency ? 'Gérez les contrats de vos prestataires' : 'Consultez et signez vos contrats'}
              </p>
            </div>
            {isAgency && (
              <Button 
                onClick={() => setShowCreateModal(true)} 
                style={{ backgroundColor: theme.colors?.accent }}
                className="relative z-10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau contrat
              </Button>
            )}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList>
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="pending">En attente</TabsTrigger>
              <TabsTrigger value="active">Actifs</TabsTrigger>
              {isAgency ? (
                <>
                  <TabsTrigger value="monthly">Créatifs</TabsTrigger>
                  <TabsTrigger value="mission">Sous-traitants</TabsTrigger>
                </>
              ) : (
                <TabsTrigger value="ended">Terminés</TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          {/* Contracts List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredContracts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun contrat</h3>
                <p className="text-slate-500">
                  {isAgency ? 'Créez votre premier contrat pour un membre' : 'Vous n\'avez pas encore de contrat'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredContracts.map((contract) => (
                <Card 
                  key={contract.id} 
                  className={cn(
                    "hover:shadow-md transition-shadow cursor-pointer",
                    needsAction(contract) && "ring-2 ring-amber-300"
                  )}
                  onClick={() => openDetailModal(contract)}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {getTypeBadge(contract.contract_type)}
                          {getStatusBadge(contract.status)}
                          {needsAction(contract) && (
                            <Badge className="bg-amber-500 text-white animate-pulse">Action requise</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg text-slate-800">
                          {isAgency ? contract.member_name : 'Mon contrat'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mt-2">
                          <span className="flex items-center gap-1">
                            <Euro className="w-4 h-4" />
                            {contract.pricing_type === 'fixed' 
                              ? `${contract.fixed_amount}€ forfait`
                              : `${contract.hourly_rate}€/h`
                            }
                          </span>
                          {contract.contract_type === 'monthly' ? (
                            <>
                              {contract.start_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  Du {new Date(contract.start_date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                              {contract.end_date && (
                                <span>au {new Date(contract.end_date).toLocaleDateString('fr-FR')}</span>
                              )}
                            </>
                          ) : (
                            <>
                              {contract.budget_hours && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {contract.budget_hours}h
                                </span>
                              )}
                              {contract.deadline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  Deadline: {new Date(contract.deadline).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Signatures status */}
                        <div className="flex flex-col items-end gap-1 text-xs">
                          <div className={cn(
                            "flex items-center gap-1",
                            contract.agency_signed ? "text-emerald-600" : "text-slate-400"
                          )}>
                            {contract.agency_signed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            Agence
                          </div>
                          <div className={cn(
                            "flex items-center gap-1",
                            contract.member_signed ? "text-emerald-600" : "text-slate-400"
                          )}>
                            {contract.member_signed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            Membre
                          </div>
                        </div>
                        
                        {/* Action buttons for agency */}
                        {isAgency && (
                          <div className="flex items-center gap-1">
                            {canEdit(contract) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={(e) => openEditModal(contract, e)}
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                              onClick={(e) => openDuplicateModal(contract, e)}
                              title="Dupliquer"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            {canTerminate(contract) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                onClick={(e) => openTerminateConfirm(contract, e)}
                                title="Résilier"
                              >
                                <AlertCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete(contract) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={(e) => openDeleteConfirm(contract, e)}
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Nouveau contrat
            </DialogTitle>
            <DialogDescription>
              Créez un contrat pour un membre de l&apos;équipe
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Contract Type */}
            <div className="space-y-2">
              <Label>Type de contrat *</Label>
              <Select value={contractType} onValueChange={setContractType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Créatif (récurrent)</SelectItem>
                  <SelectItem value="mission">Sous-traitant (ponctuel)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Member Selection */}
            <div className="space-y-2">
              <Label>Membre *</Label>
              <Select value={selectedMember} onValueChange={setSelectedMember}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un membre" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pricing Type - Only for sous-traitant contracts */}
            {contractType === 'mission' && (
              <div className="space-y-2">
                <Label>Mode de tarification *</Label>
                <Select value={pricingType} onValueChange={setPricingType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Taux horaire</SelectItem>
                    <SelectItem value="fixed">Forfait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Pricing fields based on type */}
            {(contractType === 'monthly' || pricingType === 'hourly') && (
              <div className="space-y-2">
                <Label>Taux horaire (€/h) *</Label>
                <Input 
                  type="number"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="Ex: 45"
                />
              </div>
            )}
            
            {contractType === 'mission' && pricingType === 'fixed' && (
              <div className="space-y-2">
                <Label>Montant forfaitaire (€ HT) *</Label>
                <Input 
                  type="number"
                  value={fixedAmount}
                  onChange={(e) => setFixedAmount(e.target.value)}
                  placeholder="Ex: 1500"
                />
                <p className="text-xs text-slate-500">Montant global pour la mission</p>
              </div>
            )}
            
            {contractType === 'monthly' ? (
              <>
                {/* Monthly contract fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de début</Label>
                    <Input 
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date de fin</Label>
                    <Input 
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Sous-traitant contract fields */}
                <div className="space-y-2">
                  <Label>Brief de la prestation *</Label>
                  <Textarea 
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="Décrivez la prestation..."
                    rows={3}
                  />
                </div>
                
                {/* Audio Brief */}
                <div className="space-y-2">
                  <Label>Brief audio (optionnel)</Label>
                  <div className="p-3 border rounded-lg bg-slate-50 space-y-3">
                    {!audioBlob ? (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant={isRecording ? "destructive" : "outline"}
                          size="sm"
                          onClick={isRecording ? stopRecording : startRecording}
                          className="gap-2"
                        >
                          {isRecording ? (
                            <>
                              <Square className="w-4 h-4" />
                              Arrêter
                            </>
                          ) : (
                            <>
                              <Mic className="w-4 h-4" />
                              Enregistrer
                            </>
                          )}
                        </Button>
                        {isRecording && (
                          <span className="text-sm text-red-500 animate-pulse flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                            Enregistrement...
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <audio 
                            src={URL.createObjectURL(audioBlob)} 
                            controls 
                            className="h-8 flex-1"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={deleteAudioRecording}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={transcribeAudio}
                          disabled={transcribing}
                          className="w-full gap-2"
                        >
                          {transcribing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Transcription en cours...
                            </>
                          ) : (
                            <>
                              <FileText className="w-4 h-4" />
                              Transcrire en texte
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {briefAudioTranscript && (
                      <div className="p-2 bg-white rounded border text-sm">
                        <p className="text-xs text-slate-500 mb-1">Transcription :</p>
                        <p className="text-slate-700 whitespace-pre-wrap">{briefAudioTranscript}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input 
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                  />
                </div>
                
                {/* Budget section - only for hourly pricing */}
                {pricingType === 'hourly' && (
                  <div className="space-y-2">
                    <Label>Budget heures</Label>
                    <Input 
                      type="number"
                      value={budgetHours}
                      onChange={(e) => setBudgetHours(e.target.value)}
                      placeholder="Ex: 10"
                    />
                    {budgetHours && hourlyRate && (
                      <p className="text-sm text-slate-600">
                        Budget total : <span className="font-semibold">{(parseFloat(budgetHours) * parseFloat(hourlyRate)).toFixed(2)} € HT</span>
                        <span className="text-slate-400 ml-1">
                          ({budgetHours}h × {hourlyRate}€/h)
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            
            {/* Conditions */}
            <div className="space-y-2">
              <Label>Conditions particulières</Label>
              <Textarea 
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                placeholder="Conditions spécifiques du contrat..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateContract} disabled={creating} style={{ backgroundColor: theme.colors?.accent }}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer au membre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract Detail / Negotiate Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Détail du contrat
            </DialogTitle>
          </DialogHeader>
          
          {selectedContract && (
            <div className="space-y-4 py-4">
              {/* Contract info */}
              <div className="flex items-center gap-3">
                {getTypeBadge(selectedContract.contract_type)}
                {getStatusBadge(selectedContract.status)}
              </div>
              
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                {isAgency && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Membre:</span>
                    <span className="font-medium">{selectedContract.member_name}</span>
                  </div>
                )}
                {/* Show hourly rate OR fixed amount based on pricing type */}
                {selectedContract.pricing_type === 'fixed' ? (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Forfait:</span>
                    <span className="font-medium">{selectedContract.fixed_amount}€ HT</span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Taux horaire:</span>
                    <span className="font-medium">{selectedContract.hourly_rate}€/h</span>
                  </div>
                )}
                {selectedContract.contract_type === 'monthly' ? (
                  <>
                    {selectedContract.start_date && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Période:</span>
                        <span className="font-medium">
                          {new Date(selectedContract.start_date).toLocaleDateString('fr-FR')}
                          {selectedContract.end_date && ` - ${new Date(selectedContract.end_date).toLocaleDateString('fr-FR')}`}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {selectedContract.brief && (
                      <div>
                        <span className="text-slate-500 block mb-1">Brief:</span>
                        <p className="text-sm bg-white p-2 rounded">{selectedContract.brief}</p>
                      </div>
                    )}
                    {selectedContract.budget_hours && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Budget:</span>
                        <span className="font-medium">{selectedContract.budget_hours}h ({selectedContract.budget_amount}€)</span>
                      </div>
                    )}
                    {selectedContract.deadline && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Deadline:</span>
                        <span className="font-medium">{new Date(selectedContract.deadline).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </>
                )}
                {selectedContract.conditions && (
                  <div>
                    <span className="text-slate-500 block mb-1">Conditions:</span>
                    <p className="text-sm">{selectedContract.conditions}</p>
                  </div>
                )}
              </div>
              
              {/* Pending Changes - Show proposed modifications */}
              {selectedContract.pending_changes && Object.keys(selectedContract.pending_changes).length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                  <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Modifications proposées par le membre
                  </p>
                  <div className="space-y-1 text-sm">
                    {selectedContract.pending_changes.hourly_rate && (
                      <div className="flex justify-between">
                        <span className="text-amber-700">Nouveau taux horaire:</span>
                        <span className="font-medium text-amber-900">{selectedContract.pending_changes.hourly_rate}€/h</span>
                      </div>
                    )}
                    {selectedContract.pending_changes.budget_hours && (
                      <div className="flex justify-between">
                        <span className="text-amber-700">Nouveau budget:</span>
                        <span className="font-medium text-amber-900">{selectedContract.pending_changes.budget_hours}h</span>
                      </div>
                    )}
                    {selectedContract.pending_changes.budget_amount && (
                      <div className="flex justify-between">
                        <span className="text-amber-700">Nouveau montant:</span>
                        <span className="font-medium text-amber-900">{selectedContract.pending_changes.budget_amount}€</span>
                      </div>
                    )}
                    {selectedContract.pending_changes.deadline && (
                      <div className="flex justify-between">
                        <span className="text-amber-700">Nouvelle deadline:</span>
                        <span className="font-medium text-amber-900">{new Date(selectedContract.pending_changes.deadline).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                    {selectedContract.pending_changes.conditions && (
                      <div>
                        <span className="text-amber-700 block">Nouvelles conditions:</span>
                        <p className="font-medium text-amber-900 mt-1">{selectedContract.pending_changes.conditions}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Signatures */}
              <div className="flex gap-4">
                <div className={cn(
                  "flex-1 p-3 rounded-lg border-2 text-center",
                  selectedContract.agency_signed ? "border-emerald-200 bg-emerald-50" : "border-slate-200"
                )}>
                  <CheckCircle className={cn("w-6 h-6 mx-auto mb-1", selectedContract.agency_signed ? "text-emerald-500" : "text-slate-300")} />
                  <p className="text-sm font-medium">Agence</p>
                  <p className="text-xs text-slate-500">{selectedContract.agency_signed ? "Signé" : "Non signé"}</p>
                </div>
                <div className={cn(
                  "flex-1 p-3 rounded-lg border-2 text-center",
                  selectedContract.member_signed ? "border-emerald-200 bg-emerald-50" : "border-slate-200"
                )}>
                  <CheckCircle className={cn("w-6 h-6 mx-auto mb-1", selectedContract.member_signed ? "text-emerald-500" : "text-slate-300")} />
                  <p className="text-sm font-medium">Membre</p>
                  <p className="text-xs text-slate-500">{selectedContract.member_signed ? "Signé" : "Non signé"}</p>
                </div>
              </div>
              
              {/* Negotiation history */}
              {selectedContract.negotiations?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <History className="w-4 h-4" />
                    Historique des négociations
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedContract.negotiations.map((neg, idx) => (
                      <div key={idx} className="text-xs p-2 bg-slate-50 rounded">
                        <div className="flex justify-between">
                          <span className="font-medium">{neg.user_name}</span>
                          <span className="text-slate-400">{new Date(neg.timestamp).toLocaleString('fr-FR')}</span>
                        </div>
                        <p className="text-slate-600">Action: {neg.action}</p>
                        {neg.reason && <p className="text-slate-500">Motif: {neg.reason}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions - only if pending action from current user */}
              {needsAction(selectedContract) && (
                <div className="border-t pt-4">
                  {/* Show different UI based on context */}
                  {selectedContract.status === 'pending_member_sign' && !isAgency ? (
                    // Member needs to sign after agency accepted counter-proposal
                    <div className="space-y-4">
                      <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <p className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Votre contre-proposition a été acceptée par l'agence
                        </p>
                        <p className="text-xs text-emerald-700 mt-1">
                          Veuillez signer le contrat pour finaliser l'accord.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={negotiateAction === 'accept' ? 'default' : 'outline'}
                          onClick={() => setNegotiateAction('accept')}
                          className={cn("flex-1", negotiateAction === 'accept' ? 'bg-emerald-500' : '')}
                        >
                          <FileSignature className="w-4 h-4 mr-2" />
                          Signer le contrat
                        </Button>
                        <Button
                          variant={negotiateAction === 'reject' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNegotiateAction('reject')}
                          className={negotiateAction === 'reject' ? 'bg-red-500' : ''}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Standard negotiation UI
                    <>
                      <p className="text-sm font-medium mb-3">
                        {isAgency ? 'Votre décision sur la proposition' : 'Votre décision'}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Button
                          variant={negotiateAction === 'accept' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNegotiateAction('accept')}
                          className={negotiateAction === 'accept' ? 'bg-emerald-500' : ''}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {isAgency ? 'Accepter' : 'Signer'}
                        </Button>
                        <Button
                          variant={negotiateAction === 'modify' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNegotiateAction('modify')}
                          className={negotiateAction === 'modify' ? 'bg-blue-500' : ''}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {isAgency ? 'Contre-proposer' : 'Négocier'}
                        </Button>
                        <Button
                          variant={negotiateAction === 'reject' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNegotiateAction('reject')}
                          className={negotiateAction === 'reject' ? 'bg-red-500' : ''}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                      </div>
                      
                      {negotiateAction === 'modify' && (
                        <div className="space-y-3 mb-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700">
                            {isAgency ? 'Proposez vos modifications au membre :' : 'Proposez vos modifications à l\'agence :'}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Nouveau taux (€/h)</Label>
                              <Input
                                type="number"
                                value={proposedRate}
                                onChange={(e) => setProposedRate(e.target.value)}
                                placeholder={selectedContract.hourly_rate}
                                className="h-8"
                              />
                            </div>
                            {selectedContract.contract_type === 'mission' && (
                              <>
                                <div>
                                  <Label className="text-xs">Budget (heures)</Label>
                                  <Input
                                    type="number"
                                    value={proposedBudget}
                                    onChange={(e) => setProposedBudget(e.target.value)}
                                    placeholder={selectedContract.budget_hours}
                                    className="h-8"
                                  />
                                </div>
                                <div className="sm:col-span-2">
                                  <Label className="text-xs">Nouvelle deadline</Label>
                                  <Input
                                    type="date"
                                    value={proposedDeadline}
                                    onChange={(e) => setProposedDeadline(e.target.value)}
                                    className="h-8"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {(negotiateAction === 'reject' || negotiateAction === 'modify') && (
                    <div className="space-y-2 mb-4">
                      <Label className="text-xs">Motif {negotiateAction === 'reject' ? '(obligatoire)' : '(optionnel)'}</Label>
                      <Textarea
                        value={negotiateReason}
                        onChange={(e) => setNegotiateReason(e.target.value)}
                        placeholder="Expliquez votre décision..."
                        rows={2}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => viewContractDocument(selectedContract?.id)}
                className="flex-1"
              >
                <Eye className="w-4 h-4 mr-2" />
                Voir le document
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  viewContractDocument(selectedContract?.id);
                  setTimeout(() => downloadContractPdf(), 500);
                }}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              {needsAction(selectedContract) && negotiateAction && (
                <Button 
                  onClick={handleNegotiate} 
                  disabled={negotiating || (negotiateAction === 'reject' && !negotiateReason)}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {negotiating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {getActionButtonLabel(selectedContract, negotiateAction)}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document View Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Contrat de Sous-Traitance
            </DialogTitle>
            <DialogDescription>
              Document légal généré automatiquement
            </DialogDescription>
          </DialogHeader>
          
          {loadingDocument ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Génération du document...</span>
            </div>
          ) : (
            <div className="flex-1 overflow-auto border rounded-lg bg-white">
              <div 
                className="p-4"
                dangerouslySetInnerHTML={{ __html: documentHtml }}
              />
            </div>
          )}
          
          <DialogFooter className="shrink-0 flex-row gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDocumentModal(false)}>
              Fermer
            </Button>
            <Button variant="outline" onClick={printContract}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            <Button onClick={downloadContractPdf} style={{ backgroundColor: theme.colors?.accent }}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Modifier le contrat
            </DialogTitle>
            <DialogDescription>
              Modifier les informations du contrat de {editingContract?.member_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Contract Type */}
            <div className="space-y-2">
              <Label>Type de contrat</Label>
              <Select 
                value={editForm.contract_type || ''} 
                onValueChange={(v) => setEditForm({...editForm, contract_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Créatif (récurrent)</SelectItem>
                  <SelectItem value="mission">Sous-traitant (ponctuel)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pricing */}
            <div className="space-y-2">
              <Label>Type de tarification</Label>
              <Select 
                value={editForm.pricing_type || ''} 
                onValueChange={(v) => setEditForm({...editForm, pricing_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">À l&apos;heure</SelectItem>
                  <SelectItem value="fixed">Forfait</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editForm.pricing_type === 'hourly' ? (
              <div className="space-y-2">
                <Label>Taux horaire (€/h)</Label>
                <Input
                  type="number"
                  value={editForm.hourly_rate || ''}
                  onChange={(e) => setEditForm({...editForm, hourly_rate: e.target.value})}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Montant forfait (€)</Label>
                <Input
                  type="number"
                  value={editForm.fixed_amount || ''}
                  onChange={(e) => setEditForm({...editForm, fixed_amount: e.target.value})}
                />
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={editForm.start_date || ''}
                  onChange={(e) => setEditForm({...editForm, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={editForm.end_date || ''}
                  onChange={(e) => setEditForm({...editForm, end_date: e.target.value})}
                />
              </div>
            </div>

            {editForm.contract_type === 'mission' && (
              <>
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={editForm.deadline || ''}
                    onChange={(e) => setEditForm({...editForm, deadline: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget heures</Label>
                    <Input
                      type="number"
                      value={editForm.budget_hours || ''}
                      onChange={(e) => setEditForm({...editForm, budget_hours: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Budget montant (€)</Label>
                    <Input
                      type="number"
                      value={editForm.budget_amount || ''}
                      onChange={(e) => setEditForm({...editForm, budget_amount: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Brief */}
            <div className="space-y-2">
              <Label>Brief / Conditions</Label>
              <Textarea
                value={editForm.brief || ''}
                onChange={(e) => setEditForm({...editForm, brief: e.target.value})}
                rows={3}
              />
            </div>

            {/* Prestataire info */}
            <div className="space-y-2">
              <Label>Statut prestataire</Label>
              <Select 
                value={editForm.prestataire_statut || ''} 
                onValueChange={(v) => setEditForm({...editForm, prestataire_statut: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Micro-entrepreneur">Micro-entrepreneur</SelectItem>
                  <SelectItem value="Auto-entrepreneur">Auto-entrepreneur</SelectItem>
                  <SelectItem value="SASU">SASU</SelectItem>
                  <SelectItem value="EURL">EURL</SelectItem>
                  <SelectItem value="SAS">SAS</SelectItem>
                  <SelectItem value="SARL">SARL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Contract Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-violet-600" />
              Dupliquer le contrat
            </DialogTitle>
            <DialogDescription>
              Créer une copie du contrat de {duplicatingContract?.member_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Member Selection */}
            <div className="space-y-2">
              <Label>Attribuer à</Label>
              <Select 
                value={duplicateForm.member_id || ''} 
                onValueChange={(v) => setDuplicateForm({...duplicateForm, member_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Même membre" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Laissez vide pour le même membre</p>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nouvelle date de début</Label>
                <Input
                  type="date"
                  value={duplicateForm.start_date || ''}
                  onChange={(e) => setDuplicateForm({...duplicateForm, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Nouvelle date de fin</Label>
                <Input
                  type="date"
                  value={duplicateForm.end_date || ''}
                  onChange={(e) => setDuplicateForm({...duplicateForm, end_date: e.target.value})}
                />
              </div>
            </div>

            {duplicatingContract?.contract_type === 'mission' && (
              <div className="space-y-2">
                <Label>Nouvelle deadline</Label>
                <Input
                  type="date"
                  value={duplicateForm.deadline || ''}
                  onChange={(e) => setDuplicateForm({...duplicateForm, deadline: e.target.value})}
                />
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
              <p className="font-medium mb-1">Ce qui sera copié :</p>
              <ul className="list-disc list-inside text-xs space-y-0.5">
                <li>Type de contrat et tarification</li>
                <li>Brief et conditions</li>
                <li>Règles bonus/malus</li>
                <li>Informations prestataire</li>
              </ul>
              <p className="mt-2 text-amber-600 font-medium">Le nouveau contrat sera créé en brouillon.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleDuplicate}
              disabled={duplicating}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {duplicating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer le contrat
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-700">
              Êtes-vous sûr de vouloir supprimer définitivement le contrat de{' '}
              <span className="font-semibold">{deletingContract?.member_name}</span> ?
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={deleting}
              variant="destructive"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminate Confirmation Dialog */}
      <Dialog open={showTerminateConfirm} onOpenChange={setShowTerminateConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-5 h-5" />
              Résilier le contrat
            </DialogTitle>
            <DialogDescription>
              Le contrat sera marqué comme résilié et le membre sera notifié.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-700">
              Vous êtes sur le point de résilier le contrat de{' '}
              <span className="font-semibold">{terminatingContract?.member_name}</span>.
            </p>
            
            <div className="space-y-2">
              <Label>Motif de résiliation (optionnel)</Label>
              <Textarea
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                placeholder="Ex: Fin de collaboration, Non-respect des conditions..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTerminateConfirm(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleTerminate}
              disabled={terminating}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {terminating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Résilier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberContractsPage;
